import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import Select from 'react-select';

import FilledButton from 'components/application/buttons/FilledButton';
import CircleQuestion from 'components/application/CircleQuestion';
import {
  DropdownIndicator,
  Option,
} from 'components/application/CollectionSelect';
import CopyableField from 'components/application/CopyableField';
import ReadOnlyField from 'components/application/ReadOnlyField';
import Tooltip from 'components/application/Tooltip';

import { identityProviderNames } from 'components/helpers/identityProviders';

function formatValue(value) {
  return { label: identityProviderNames[value], value };
}

export default function EnableFormCard({
  assertionConsumerServiceUrl,
  domainNames,
  formValues,
  isEnforced,
  onInputChange,
  onNameChange,
  onSubmit,
  providerNames,
  removeErrorStyling,
  requestError,
  serviceProviderMetadataUrl,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.singleSignOn.components.EnableFormCard',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const isFieldInvalid = (field) => {
    const validationError = requestError.validationErrors[field];

    return !!validationError && validationError.fieldHighlighted;
  };

  return (
    <div className='section tw-border-grey-100 tw-bg-white tw-p-10'>
      <h3 className='tw-mb-2 tw-mt-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
        {i18nT('title')}
      </h3>
      <p className='tw-m-0 tw-mb-8'>
        <Trans
          components={{
            a: (
              /* eslint-disable jsx-a11y/anchor-has-content */
              <a
                className='app-link va-baseline tw-font-medium tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                href='https://intercom.help/hands-hq/en/articles/6887426-guide-to-single-sign-on-saml-sso'
                rel='noreferrer'
                target='_blank'
              />
              /* eslint-enable jsx-a11y/anchor-has-content */
            ),
          }}
          i18nKey='settings.singleSignOn.components.EnableFormCard.helpGuide'
          ns='components'
        />
      </p>
      <div className='form-group tw-m-0'>
        <label className='hhq-checkbox__label tooltip-parent' htmlFor='enabled'>
          <input
            checked={formValues.enabled}
            className='hhq-checkbox__input'
            disabled={isEnforced}
            id='enabled'
            name='enabled'
            onChange={onInputChange}
            type='checkbox'
            value='enabled'
          />
          <span
            className={classNames(
              'hhq-checkbox__toggle tw-cursor-pointer tw-border-2 tw-font-bold tw-text-white',
              formValues.enabled ?
                'tw-border-blue-400 tw-bg-blue-400 hover:tw-border-blue-500 hover:tw-bg-blue-500'
              : 'tw-border-grey-300 tw-bg-white hover:tw-border-blue-300 hover:tw-bg-grey-025',
            )}
          />
          {isEnforced && (
            <div className='tw-pos-abs tw-left-0 tw-top-0 tw-h-5.5 tw-w-5.5'>
              <Tooltip
                placement='top'
                tooltip={i18nT('form.enabled.tooltip')}
                trigger='hover'
              />
            </div>
          )}
          <span className='tw-ml-3 tw-font-normal tw-tracking-auto'>
            {i18nT('form.enabled.label')}
          </span>
        </label>
      </div>
      {formValues.enabled && (
        <>
          <div className='form-group tw-mb-8 tw-mt-8'>
            <label
              className='field__label tw-font-medium'
              htmlFor='domain_names'
            >
              <span>{i18nT('form.domainNames.label')}</span>
              <Tooltip
                placement='top'
                tooltip={i18nT('form.domainNames.tooltip')}
                trigger='hover'
              >
                <CircleQuestion />
              </Tooltip>
            </label>
            <div className='tw-flex'>
              <ReadOnlyField name='domain_names' value={domainNames} />
              <div className='tw-h-10 tw-w-10' />
            </div>
          </div>
          <div className='form-group tw-mb-8'>
            <CopyableUrl
              labelText={i18nT('form.serviceProviderMetadataUrl.label')}
              name='service_provider_metadata_url'
              tooltipText={i18nT('form.serviceProviderMetadataUrl.tooltip')}
              value={serviceProviderMetadataUrl}
            />
          </div>
          <div className='form-group tw-mb-8'>
            <CopyableUrl
              labelText={i18nT('form.serviceProviderSingleSignOnUrl.label')}
              name='assertion_consumer_service_url'
              tooltipText={i18nT('form.serviceProviderSingleSignOnUrl.tooltip')}
              value={assertionConsumerServiceUrl}
            />
          </div>
          <hr className='tw-m-0 tw-my-8 tw-h-px tw-border-0 tw-bg-grey-100' />
          <div className='form-group tw-mb-8 tw-w-1/2'>
            <label
              className='field__label tw-font-medium'
              htmlFor='providerName'
            >
              <span>{i18nT('form.providerName.label')} *</span>
            </label>
            <Select
              className='collection-select__select-container'
              classNamePrefix='collection-select'
              components={{ DropdownIndicator, Option }}
              id='providerName'
              name='providerName'
              onChange={onNameChange}
              options={providerNames.map((name) => formatValue(name))}
              value={formatValue(formValues.providerName)}
            />
          </div>
          <div className='form-group tw-mb-8'>
            <label className='field__label tw-font-medium' htmlFor='targetUrl'>
              <span>{i18nT('form.targetUrl.label')} *</span>
              <Tooltip
                placement='top'
                tooltip={i18nT('form.targetUrl.tooltip')}
                trigger='hover'
              >
                <CircleQuestion />
              </Tooltip>
            </label>
            <input
              className={classNames(
                'field__input tw-font-inter tw-text-m tw-font-normal tw-tracking-auto',
                isFieldInvalid('targetUrl') ?
                  'tw-border-red-600 focus-within:tw-border-red-600 hover:tw-border-red-600 hover:focus-within:tw-border-red-600'
                : 'tw-border-grey-300 focus-within:tw-border-blue-300 hover:tw-border-grey-400 hover:focus-within:tw-border-blue-300',
              )}
              id='targetUrl'
              name='targetUrl'
              onChange={onInputChange}
              onFocus={removeErrorStyling}
              type='text'
              value={formValues.targetUrl}
            />
          </div>
          <div className='form-group'>
            <label
              className='field__label tw-font-medium'
              htmlFor='certificate'
            >
              <span>{i18nT('form.certificate.label')} *</span>
              <Tooltip
                placement='top'
                tooltip={i18nT('form.certificate.tooltip')}
                trigger='hover'
              >
                <CircleQuestion />
              </Tooltip>
            </label>
            <textarea
              className={classNames(
                'field__input field__input--textarea tw-h-80 tw-font-inter tw-text-m tw-font-normal tw-tracking-auto',
                isFieldInvalid('certificate') ?
                  'tw-border-red-600 focus-within:tw-border-red-600 hover:tw-border-red-600 hover:focus-within:tw-border-red-600'
                : 'tw-border-grey-300 focus-within:tw-border-blue-300 hover:tw-border-grey-400 hover:focus-within:tw-border-blue-300',
              )}
              id='certificate'
              name='certificate'
              onChange={onInputChange}
              onFocus={removeErrorStyling}
              rows='12'
              spellCheck='false'
              value={formValues.certificate}
            />
          </div>
        </>
      )}
      <FilledButton className='tw-mt-10' color='blue' onClick={onSubmit}>
        {i18nTShared('saveChanges')}
      </FilledButton>
    </div>
  );
}

function CopyableUrl({ labelText, name, tooltipText, value }) {
  return (
    <>
      <label className='field__label tw-font-medium' htmlFor={name}>
        <span>{labelText}</span>
        <Tooltip placement='top' tooltip={tooltipText} trigger='hover'>
          <CircleQuestion />
        </Tooltip>
      </label>
      <CopyableField name={name} value={value} />
    </>
  );
}

EnableFormCard.propTypes = {
  domainNames: PropTypes.string.isRequired,
  serviceProviderMetadataUrl: PropTypes.string.isRequired,
  assertionConsumerServiceUrl: PropTypes.string.isRequired,
  providerNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  formValues: PropTypes.object.isRequired,
  isEnforced: PropTypes.bool.isRequired,
  requestError: PropTypes.object.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
